import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../context/AuthContext';
import { clearSessionState } from '../../utils/sessionStorage';
import { adminApi } from '../../services/api';

const LoginPage = () => {
  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [selectedHealthSystemId, setSelectedHealthSystemId] = useState('');

  // User type and health systems
  const [userTypeData, setUserTypeData] = useState(null);
  const [healthSystems, setHealthSystems] = useState([]);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);

  // Manual token state
  const [manualToken, setManualToken] = useState('');

  // Common state
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('email-password');

  const { user } = useAuthContext();
  const navigate = useNavigate();

  // Helper function to get redirect URL after login
  const getRedirectUrl = () => {
    const savedRedirect = localStorage.getItem('redirect_after_login');
    if (savedRedirect && savedRedirect !== '/admin/login') {
      localStorage.removeItem('redirect_after_login');
      return savedRedirect;
    }
    return '/admin';
  };

  // Redirect if already logged in (this handles the case where someone navigates to /login while already authenticated)
  // We don't check for saved redirect here because this only fires when someone manually goes to /login while logged in
  useEffect(() => {
    if (user) {
      navigate('/admin');
    }
  }, [user, navigate]);

  // Clear patient session data when navigating to the admin login page
  useEffect(() => {
    clearSessionState();
    // console.log('Patient session data cleared on admin login page load.');
  }, []);

  // Handle embed error from localStorage when launched in embedded mode
  useEffect(() => {
    const embedError = localStorage.getItem('embed_token_error');
    if (embedError) {
      setError(`Embedded launch error: ${embedError}`);
      setActiveTab('manual-token');

      // Clear the error from localStorage after a delay to ensure it persists through re-mounts
      setTimeout(() => {
        localStorage.removeItem('embed_token_error');
      }, 2000);
    }
  }, []);

  // Handle logout success message and session expiration from query params
  useEffect(() => {
    // Read from window.location.search directly as primary source
    const urlParams = new URLSearchParams(window.location.search);
    const logoutParam = urlParams.get('logout');
    const sessionExpiredParam = urlParams.get('session_expired');

    if (logoutParam === 'success') {
      setSuccessMessage('You have been successfully logged out. Thanks for using Ankr VCX!');
      // Clear the query parameter from URL after component renders
      setTimeout(() => {
        window.history.replaceState({}, '', '/admin/login');
      }, 500);
      // Auto-dismiss message after 30 seconds
      setTimeout(() => setSuccessMessage(''), 30000);
    } else if (sessionExpiredParam === 'true') {
      setError('Sorry, your session has expired. Let\'s get you logged in again for security purposes.');
      // Clear the query parameter from URL after component renders
      setTimeout(() => {
        window.history.replaceState({}, '', '/admin/login');
      }, 500);
      // Auto-dismiss error after 30 seconds
      setTimeout(() => setError(''), 30000);
    }
  }, []);

  // Validate email and fetch user type
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsCheckingEmail(true);

    try {
      const response = await adminApi.checkUserType(email);

      if (response.data.success) {
        setUserTypeData(response.data);

        // If healthcare user with multiple health systems, populate the dropdown
        if (response.data.user_type === 'healthcare' &&
            response.data.user_info?.health_system_memberships?.length > 0) {
          setHealthSystems(response.data.user_info.health_system_memberships);

          // Auto-select primary health system
          const primarySystem = response.data.user_info.health_system_memberships.find(m => m.is_primary);
          if (primarySystem) {
            setSelectedHealthSystemId(primarySystem.health_system_id);
          } else {
            // Select first one if no primary
            setSelectedHealthSystemId(response.data.user_info.health_system_memberships[0].health_system_id);
          }
        } else {
          setHealthSystems([]);
          setSelectedHealthSystemId('');
        }
      } else {
        setError(response.data.error || 'Failed to verify email');
      }
    } catch (err) {
      console.error('Email check error:', err);
      setError(err.response?.data?.error || 'An error occurred. Please try again.');
    } finally {
      setIsCheckingEmail(false);
    }
  };

  // Handle login form submission
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Determine auth endpoint
      const authEndpoint = userTypeData?.auth_endpoint || '/admin/login/';

      // Call the userLogin function with health system ID if available
      const response = await adminApi.userLogin(
        email,
        password,
        authEndpoint,
        selectedHealthSystemId || null
      );

      if (response.data.success) {
        // Store the API token and metadata
        localStorage.setItem('admin_api_token', response.data.api_token);
        localStorage.setItem('admin_tokens', JSON.stringify({
          api_token_id: response.data.api_token_id,
          expires_at: response.data.expires_at,
          token_type: response.data.token_type,
          health_system_id: response.data.health_system_id || null
        }));

        // Redirect to saved path or admin dashboard
        const redirectUrl = getRedirectUrl();
        window.location.href = redirectUrl;
      } else {
        setError(response.data.error || 'Login failed');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.response?.data?.error || 'Invalid username or password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleManualTokenSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await adminApi.verifyLoginToken(manualToken);

      if (response.data.success) {
        const { api_token_id, api_token_type, expires_at } = response.data;

        localStorage.setItem('admin_api_token', manualToken);
        localStorage.setItem('admin_tokens', JSON.stringify({
          api_token_id: api_token_id,
          expires_at: expires_at,
          token_type: api_token_type
        }));

        // Redirect to saved path or admin dashboard
        const redirectUrl = getRedirectUrl();
        window.location.href = redirectUrl;
      } else {
        setError(response.data.error || 'API Token verification failed.');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'An error occurred during token verification.');
      localStorage.removeItem('admin_api_token');
      localStorage.removeItem('admin_tokens');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container">
      <div className="row justify-content-center mt-5">
        <div className="col-sm-6">
          <div className="card">
            <div className="card-body">
              <h2 className="card-title text-center text-lg mb-4">Admin Login</h2>

              {successMessage && (
                <div className="alert alert-success fade show" role="alert">
                  <i className="fas fa-check-circle me-2"></i>
                  {successMessage}
                </div>
              )}

              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}

              {/* Bootstrap Tabs */}
              <ul className="nav nav-tabs mb-4" id="loginTabs" role="tablist">
                <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link ${activeTab === 'email-password' ? 'active' : ''}`}
                    id="email-password-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#email-password"
                    type="button"
                    role="tab"
                    aria-controls="email-password"
                    aria-selected={activeTab === 'email-password'}
                    onClick={() => setActiveTab('email-password')}
                  >
                    Email/Password
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link ${activeTab === 'manual-token' ? 'active' : ''}`}
                    id="manual-token-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#manual-token"
                    type="button"
                    role="tab"
                    aria-controls="manual-token"
                    aria-selected={activeTab === 'manual-token'}
                    onClick={() => setActiveTab('manual-token')}
                  >
                    API Token
                  </button>
                </li>
              </ul>

              {/* Tab Content */}
              <div className="tab-content" id="loginTabsContent">
                {/* Email/Password Tab - Conditional Multi-Step Form */}
                <div
                  className={`tab-pane fade ${activeTab === 'email-password' ? 'show active' : ''}`}
                  id="email-password"
                  role="tabpanel"
                  aria-labelledby="email-password-tab"
                >
                  {!userTypeData ? (
                    // Step 1: Email validation
                    <form onSubmit={handleEmailSubmit} className="mt-3">
                      <div className="mb-3">
                        <label htmlFor="email" className="form-label">Email or Username</label>
                        <input
                          type="text"
                          className="form-control"
                          id="email"
                          name="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder=""
                          required
                          autoFocus
                          disabled={isCheckingEmail}
                        />
                        {isCheckingEmail && (
                          <small className="text-muted">Checking user...</small>
                        )}
                      </div>

                      <div className="d-grid">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={isCheckingEmail}
                        >
                          {isCheckingEmail ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              Checking...
                            </>
                          ) : (
                            'Continue'
                          )}
                        </button>
                      </div>
                    </form>
                  ) : (
                    // Step 2: Password and login (after email validated)
                    <form onSubmit={handleLoginSubmit} className="mt-3">
                      {/* Show user info */}
                      {/* {userTypeData.user_info && (
                        <div className="alert alert-info mb-3">
                          <strong>Welcome back!</strong>
                          <div className="mt-2">
                            <small>
                              {userTypeData.user_info.first_name && userTypeData.user_info.last_name && (
                                <div>{userTypeData.user_info.first_name} {userTypeData.user_info.last_name}</div>
                              )}
                              {userTypeData.user_info.email && (
                                <div>{userTypeData.user_info.email}</div>
                              )}
                              {userTypeData.user_type && (
                                <div className="text-muted">
                                  User type: <span className="badge bg-secondary">{userTypeData.user_type}</span>
                                </div>
                              )}
                            </small>
                          </div>
                        </div>
                      )} */}

                      {/* Email Field (read-only) */}
                      <div className="mb-3">
                        <label htmlFor="email-readonly" className="form-label">Email or Username</label>
                        <div className="d-flex align-items-center">
                          <input type="text" className="form-control" id="email-readonly" value={email} readOnly />
                          <i className='fas fa-check-circle fa-lg text-success ml-2'></i>
                          </div>
                      </div>

                      {/* Health System Selector (conditional - only for healthcare users with multiple systems) */}
                      {healthSystems.length > 1 && (
                        <div className="mb-3">
                          <label htmlFor="healthSystem" className="form-label">
                            Health System <span className="text-danger">*</span>
                          </label>
                          <select
                            className="form-select"
                            id="healthSystem"
                            value={selectedHealthSystemId}
                            onChange={(e) => setSelectedHealthSystemId(e.target.value)}
                            required
                            disabled={isLoading}
                          >
                            {healthSystems.map((membership) => (
                              <option key={membership.health_system_id} value={membership.health_system_id}>
                                {membership.health_system_name}
                                {membership.is_primary && ' (Primary)'}
                              </option>
                            ))}
                          </select>
                          <small className="text-muted">
                            Select the health system you want to access
                          </small>
                        </div>
                      )}

                      {/* Password Field */}
                      <div className="mb-3">
                        <label htmlFor="password" className="form-label">Password</label>
                        <input
                          type="password"
                          className="form-control"
                          id="password"
                          name="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder=""
                          required
                          autoFocus
                          disabled={isLoading}
                        />
                      </div>

                      {/* Buttons */}
                      <div className="d-grid gap-2">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={isLoading}
                        >
                          {isLoading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              Logging in...
                            </>
                          ) : (
                            'Login'
                          )}
                        </button>

                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() => {
                            setUserTypeData(null);
                            setHealthSystems([]);
                            setSelectedHealthSystemId('');
                            setPassword('');
                            setError('');
                          }}
                          disabled={isLoading}
                        >Back
                        </button>
                      </div>
                    </form>
                  )}

                  <div className="mt-4 text-center">
                    <p className="text-muted">
                      <small>
                        <code>Note:</code> All access attempts are logged for security purposes.
                      </small>
                    </p>
                  </div>
                </div>

                {/* Manual Token Tab */}
                <div
                  className={`tab-pane fade ${activeTab === 'manual-token' ? 'show active' : ''}`}
                  id="manual-token"
                  role="tabpanel"
                  aria-labelledby="manual-token-tab"
                >
                  <div className="mt-3">
                    <form onSubmit={handleManualTokenSubmit}>
                      <div className="mb-3">
                        {/* <label htmlFor="manualToken" className="form-label">API Token</label> */}
                        <textarea
                          className="form-control"
                          id="manualToken"
                          rows="5"
                          value={manualToken}
                          onChange={(e) => setManualToken(e.target.value)}
                          placeholder="Paste your API token here..."
                          required
                        />
                      </div>

                      <div className="d-grid">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={isLoading}
                        >
                          {isLoading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              Processing...
                            </>
                          ) : (
                            'Login'
                          )}
                        </button>
                      </div>
                    </form>

                    <div className="mt-4 text-center">
                      <p className="text-muted">
                        <small>
                        <code>Note:</code> To generate a token, check documentation. All access attempts are logged for security purposes.
                        </small>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
